// src/utils/contracts/shared.js
import jsPDF from 'jspdf';

/** Convert blob to data URL */
export const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/** Format "YYYY-MM-DD" string to M/D/YY */
export const formatDateForContract = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit'
  });
};


export const generatePDFFromText = ({
  text,
  name,
  logoData,
  headerTitle,
  subHeaderTitle,
  baseFontSize = 9,
  margin = 10,
  justify = true,     // paragraph justification on by default
  headerOnFirstPage = true,
  justifyLastLine = false,   
  lastLineMinRatio = 0.75,   // only used if you turn justifyLastLine back on
  maxExtraPerSpace = 0.9 
}) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  const maxLineWidth = pageWidth - 2 * margin;

  // relative sizes based on baseFontSize
  const headerFontSize = Math.round(baseFontSize * 1.56); // ~14 when base=9
  const smallerFontSize = Math.round(baseFontSize);
  const lineHeight = Math.max(3.3, baseFontSize * 0.38); // tighter, like your sample

  let yPos = 20;

  // ---------- FIXED JUSTIFICATION HELPERS ----------
  const drawJustifiedLine = (pdfInstance, line, x, y, targetWidth) => {
    const words = line.split(' ').filter(word => word.length > 0);
    if (words.length <= 1) {
      pdfInstance.text(line, x, y);
      return;
    }

    const spaceWidth = pdfInstance.getTextWidth(' ');
    const totalTextWidth = words.reduce((sum, word) => sum + pdfInstance.getTextWidth(word), 0);
    const totalNaturalSpaces = spaceWidth * (words.length - 1);
    const naturalLineWidth = totalTextWidth + totalNaturalSpaces;
    
    // Don't justify if the line is already close to target width or would create excessive spacing
    const extraSpace = targetWidth - naturalLineWidth;
    if (extraSpace < 0 || extraSpace < spaceWidth * 0.1) {
      pdfInstance.text(line, x, y);
      return;
    }
    
    // Don't justify if it would create excessive spacing (more than 2x normal space width)
    const extraSpacePerGap = extraSpace / (words.length - 1);
    if (extraSpacePerGap > spaceWidth * 1.5) {
      pdfInstance.text(line, x, y);
      return;
    }

    // Apply justification
    let cursor = x;
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      pdfInstance.text(word, cursor, y);
      if (i < words.length - 1) {
        cursor += pdfInstance.getTextWidth(word) + spaceWidth + extraSpacePerGap;
      }
    }
  };

  const drawJustifiedSingleLine = (pdfInstance, line, x, y, targetWidth) => {
    const words = line.split(' ').filter(word => word.length > 0);
    if (words.length <= 1) {
      pdfInstance.text(line, x, y);
      return;
    }

    const spaceWidth = pdfInstance.getTextWidth(' ');
    const totalTextWidth = words.reduce((sum, word) => sum + pdfInstance.getTextWidth(word), 0);
    const totalNaturalSpaces = spaceWidth * (words.length - 1);
    const naturalLineWidth = totalTextWidth + totalNaturalSpaces;
    
    // Don't justify if the line is already close to target width
    const extraSpace = targetWidth - naturalLineWidth;
    if (extraSpace < 0 || extraSpace < spaceWidth * 0.1) {
      pdfInstance.text(line, x, y);
      return;
    }
    
    // Don't justify if it would create excessive spacing
    const extraSpacePerGap = extraSpace / (words.length - 1);
    if (extraSpacePerGap > spaceWidth * 1.5) {
      pdfInstance.text(line, x, y);
      return;
    }

    // Apply justification
    let cursor = x;
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      pdfInstance.text(word, cursor, y);
      if (i < words.length - 1) {
        cursor += pdfInstance.getTextWidth(word) + spaceWidth + extraSpacePerGap;
      }
    }
  };

  const drawParagraph = (txt, {
    font = 'helvetica',
    style = 'normal',
    size = baseFontSize,
    left = margin,
    width = maxLineWidth,
    lineH = lineHeight,
    doJustify = justify
  } = {}) => {
    pdf.setFont(font, style);
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(txt, width);

    lines.forEach((ln, idx) => {
      if (yPos > pageHeight - 25) {
        pdf.addPage(); 
        yPos = addHeader(false);
        pdf.setFont(font, style); 
        pdf.setFontSize(size);
      }

      const isLast = idx === lines.length - 1;

      // Justify mid lines, and optionally the last line if it's long enough
      if (doJustify && !isLast) {
        drawJustifiedLine(pdf, ln, left, yPos, width);
      } else if (
        doJustify && isLast && justifyLastLine &&
        (pdf.getTextWidth(ln) / width) >= lastLineMinRatio &&
        ln.indexOf(' ') !== -1
      ) {
        drawJustifiedSingleLine(pdf, ln, left, yPos, width);
      } else {
        pdf.text(ln, left, yPos);
      }
      yPos += lineH;
    });
    return yPos;
  };

  const addHeader = (isFirstPage = false) => {
    let currentY = 10;

    // logo centered
    if (logoData) {
      try {
        const logoWidth = 40;
        const logoHeight = 20;
        pdf.addImage(logoData, 'PNG', (pageWidth - logoWidth) / 2, currentY, logoWidth, logoHeight);
        currentY += 28;
      } catch {
        currentY += 10;
      }
    } else {
      currentY += 10;
    }

    if (isFirstPage && headerOnFirstPage && headerTitle) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(headerFontSize);
      pdf.text(headerTitle, pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;

      if (subHeaderTitle) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(Math.round(baseFontSize * 1.22)); // ~11 when base=9
        pdf.text(subHeaderTitle, pageWidth / 2, currentY, { align: 'center' });
        currentY += 5;
      }
    }
    return currentY;
  };

  // First page header
  yPos = addHeader(true);

// ---- pre-process lines for numbering/indent continuity ----
  const textLines = text.split('\n');
  const processedLines = [];
  
  textLines.forEach((textLine) => {
    if (textLine.trim() === '') {
      processedLines.push({ text: '', indent: margin, isListItem: false });
      return;
    }
    
    const trimmed = textLine.trim();
    
    // Check if this is a numbered or lettered list item
    const isNumberedItem = /^\d+\./.test(trimmed);
    const isLetteredItem = /^[a-z]\./.test(trimmed);
    
    if (isNumberedItem || isLetteredItem) {
  processedLines.push({ 
    text: trimmed, 
    // Give lettered items more indent than numbered
    indent: isLetteredItem ? margin + 8 : margin + 3,  
    isListItem: true,
    listType: isNumberedItem ? 'numbered' : 'lettered'
  });
} else {
  // Regular text
  processedLines.push({ 
    text: textLine, 
    indent: margin, 
    isListItem: false 
  });
    }
  });



  // state flags
  let inNoPurchaseBlock = false;

  // iterate lines with special markers/format
  processedLines.forEach((lineObj) => {
    const line = typeof lineObj === 'string' ? lineObj : lineObj.text;
    const xPos = typeof lineObj === 'object' && lineObj.indent ? lineObj.indent : margin;

    // page break
    if (yPos > pageHeight - 25) {
      pdf.addPage();
      yPos = addHeader(false);
    }

    const trimmed = line.trim();

    // Center + bold CONTRACT line and VALID EVENT DATE(S) line
if (/^CONTRACT\s*#/i.test(trimmed) || /^VALID\s+EVENT\s+DATE/i.test(trimmed)) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(baseFontSize + 1); // slightly larger looks nicer
  pdf.text(trimmed, pageWidth / 2, yPos, { align: 'center' });
  yPos += lineHeight + 1;            // small spacing after
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(baseFontSize);
  return;
}

// Center + bold VERIFICATION AFFIDAVIT line
if (/^VERIFICATION\s+AFFIDAVIT\s+OF\s+PROMOTION\s+ADMINISTRATOR/i.test(trimmed)) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(baseFontSize + 1);
  pdf.text(trimmed, pageWidth / 2, yPos, { align: 'center' });
  yPos += lineHeight + 2;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(baseFontSize);
  return;
}

// Center + bold ATTENTION line
if (/^ATTENTION/i.test(trimmed)) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(baseFontSize);
  pdf.text(trimmed, pageWidth / 2, yPos, { align: 'center' });
  yPos += lineHeight + 2;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(baseFontSize);
  return;
}




    // SIGNATURE FORM SECTION
    if (trimmed.startsWith('SIGNATURE_FORM_SECTION:')) {
     yPos += 6;
      pdf.setFont('helvetica', 'bold'); 
      pdf.setFontSize(baseFontSize);
      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('DATE:', margin, yPos);
      yPos += lineHeight + 4;

      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('SIGNATURE:', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('STREET ADDRESS:', margin + 95, yPos - 5);
      yPos += lineHeight + 2;

      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('NAME (Please print):', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('CITY, STATE AND ZIP CODE:', margin + 95, yPos - 5);
      yPos += lineHeight + 2;

      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('DRIVER\'S LICENSE #:', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('TELEPHONE #:', margin + 95, yPos - 5);
      yPos += lineHeight + 2;

            pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('EMAIL ADDRESS:', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('WINNING NUMBER:', margin + 95, yPos - 5);
      yPos += lineHeight + 8;


      return;
    }

    // ADMINISTRATOR SIGNATURE SECTION
    if (trimmed.startsWith('ADMINISTRATOR_SIGNATURE_SECTION:')) {
      yPos += 8;
      pdf.setFont('helvetica', 'bold'); 
      pdf.setFontSize(baseFontSize);
      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('SIGNATURE:', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('TELEPHONE # (Work):', margin + 95, yPos - 5);
      yPos += lineHeight + 8;

      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('NAME & TITLE OF SPONSOR REPRESENTATIVE (Please Print)', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('TELEPHONE # (Home):', margin + 95, yPos - 5);
      yPos += lineHeight + 8;

      pdf.text('_________________________________________________________', margin, yPos);
      yPos += lineHeight + 1;
      pdf.text('DATE:', margin, yPos);
      pdf.text('_________________________________________________________', margin + 95, yPos - 4);
      yPos += lineHeight + 1;
      pdf.text('NAME OF COMPANY:', margin + 95, yPos - 5);
      yPos += lineHeight - 2;


      return;
    }

    // FOOTER (bold + centered)
    if (trimmed.startsWith('FOOTER_SECTION:')) {
      const footerText = trimmed.replace('FOOTER_SECTION:', '');
      yPos += 8;
      pdf.setFont('helvetica', 'bold'); 
      pdf.setFontSize(baseFontSize);
      const footerLines = pdf.splitTextToSize(footerText, maxLineWidth);
      footerLines.forEach((fl) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          yPos = addHeader(false);
          pdf.setFont('helvetica', 'bold'); 
          pdf.setFontSize(baseFontSize);
        }
        pdf.text(fl, pageWidth / 2, yPos, { align: 'center' });
        yPos += lineHeight;
      });
      pdf.setFont('helvetica', 'normal'); 
      pdf.setFontSize(baseFontSize);
      return;
    }

    // BLACK HEADER BAR
    if (trimmed.startsWith('HEADER_SECTION_BLACK:')) {
      const headerText = trimmed.replace('HEADER_SECTION_BLACK:', '');
      pdf.setFillColor(0, 0, 0);
      pdf.rect(margin, yPos - 3, maxLineWidth, 8, 'F');
      pdf.setFont('helvetica', 'bold'); 
      pdf.setFontSize(baseFontSize);
      pdf.setTextColor(255, 255, 255);
      pdf.text(headerText, pageWidth / 2, yPos + 2, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      yPos += 6;
      return;
    }

    // SMALLER_BOLD_ITALIC (justified; last line of whole block centered)
    if (trimmed.startsWith('SMALLER_BOLD_ITALIC:')) {
      const txt = trimmed.replace('SMALLER_BOLD_ITALIC:', '');
      const paragraphs = txt.split('\n\n').filter(p => p.trim());
      paragraphs.forEach((p, pIdx) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage(); 
          yPos = addHeader(false);
        }
        pdf.setFont('helvetica', 'bolditalic'); 
        pdf.setFontSize(smallerFontSize);
        const lines = pdf.splitTextToSize(p.trim(), maxLineWidth - 5);
        lines.forEach((ln, lnIdx) => {
          if (yPos > pageHeight - 25) {
            pdf.addPage(); 
            yPos = addHeader(false);
            pdf.setFont('helvetica', 'bolditalic'); 
            pdf.setFontSize(smallerFontSize);
          }
          if (lnIdx < lines.length - 1) {
            drawJustifiedLine(pdf, ln, margin, yPos, maxLineWidth - 5);
          } else if (pIdx === paragraphs.length - 1 && lnIdx === lines.length - 1) {
            pdf.text(ln, pageWidth / 2, yPos, { align: 'center' });
          } else {
            drawJustifiedLine(pdf, ln, margin, yPos, maxLineWidth - 5);
          }
          yPos += lineHeight;
        });
        if (pIdx < paragraphs.length - 1) yPos += 2;
      });
      pdf.setFont('helvetica', 'normal'); 
      pdf.setFontSize(baseFontSize);
      yPos += 4;
      return;
    }

    // NO PURCHASE block bolding
    const startsNoPurchase = /^NO PURCHASE\b/i.test(trimmed);
    const startsNextSection = /^Promotion Dates:/i.test(trimmed);
    if (!inNoPurchaseBlock && startsNoPurchase) inNoPurchaseBlock = true;
    if (inNoPurchaseBlock && startsNextSection) inNoPurchaseBlock = false;

    // Draw NO PURCHASE disclaimer lines: bold + fully justified
    if (inNoPurchaseBlock) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(baseFontSize);
      const paraWidth = maxLineWidth; // use full page width
      const words = pdf.splitTextToSize(line, paraWidth);
      words.forEach((w, idx) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          yPos = addHeader(false);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(baseFontSize);
        }
        // fully justify *every* line including last
        drawJustifiedSingleLine(pdf, w, margin, yPos, paraWidth);
        yPos += lineHeight;
      });
      return;
    }

    // "Header: content" bold header then normal content on same line
    if (
      trimmed.startsWith('Promotion Dates:') ||
      trimmed.startsWith('Odds of winning:') ||
      trimmed.startsWith('Who May Enter:') ||
      trimmed.startsWith('How to Play:') ||
      trimmed.startsWith('Claiming the Prize:') ||
      trimmed.startsWith('Winner Notification:') ||
      trimmed.startsWith('Additional Rules') ||
      trimmed.startsWith('In Case of Disputes:') ||
      trimmed.startsWith("Winner's Name:") ||
      trimmed.startsWith('Name Removal:') ||
      trimmed.startsWith('Sponsor:') ||
      line.includes('VERIFICATION AFFIDAVIT') ||
      line.includes('IMPORTANT:')
    ) {
      const colonIndex = line.indexOf(': ');
      if (colonIndex !== -1) {
        const headerPart = line.substring(0, colonIndex + 1);
        const contentPart = line.substring(colonIndex + 2);

        pdf.setFont('helvetica', 'bold'); 
        pdf.setFontSize(baseFontSize);
        pdf.text(headerPart, xPos, yPos);
        const headerWidthBold = pdf.getTextWidth(headerPart);

        pdf.setFont('helvetica', 'normal');
        const gap = pdf.getTextWidth(' ');
        const contentX = xPos + headerWidthBold + gap;
        if (contentPart.trim()) pdf.text(contentPart, contentX, yPos);

        yPos += lineHeight;
        return;
      }
    }



    // default paragraph
    drawParagraph(line, {
      style: 'normal',
      size: baseFontSize,
      left: xPos,
      width: maxLineWidth - (xPos - margin),
      lineH: lineHeight,
      doJustify: justify
    });
  });

  return {
    name,
    pdf,
    blob: pdf.output('blob')
  };
};